import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import { requestFCMToken } from "./notifications";

// Generate or retrieve guest user ID
export function getGuestUserId(): string {
  const GUEST_ID_KEY = "temizfilo_guest_id";
  
  let guestId = localStorage.getItem(GUEST_ID_KEY);
  
  if (!guestId) {
    // Generate unique ID for this browser
    guestId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(GUEST_ID_KEY, guestId);
    console.log("🆕 Created new guest ID:", guestId);
  } else {
    console.log("📱 Retrieved existing guest ID:", guestId);
  }
  
  return guestId;
}

// Request FCM token for guest user
export async function requestGuestFCMToken(): Promise<string | null> {
  try {
    const guestId = getGuestUserId();
    
    // Check if browser supports notifications
    if (!("Notification" in window)) {
      console.log("This browser does not support notifications");
      return null;
    }

    // Request notification permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.log("Notification permission denied");
      return null;
    }

    // Get FCM messaging instance and token (reuse existing function)
    const token = await requestFCMToken(guestId);
    
    if (token) {
      console.log("✅ Guest FCM Token obtained:", token.substring(0, 20) + "...");
      
      // Save token to guests collection
      const guestRef = doc(db, "guests", guestId);
      await setDoc(guestRef, {
        guestId: guestId,
        fcmToken: token,
        fcmTokenUpdatedAt: Date.now(),
        lastActive: Date.now(),
        browserInfo: {
          userAgent: navigator.userAgent,
          language: navigator.language,
        }
      }, { merge: true });
      
      return token;
    }
    
    return null;
  } catch (error) {
    console.error("Error getting guest FCM token:", error);
    return null;
  }
}

// Update guest's last active timestamp
export async function updateGuestActivity(appointmentId?: string): Promise<void> {
  try {
    const guestId = getGuestUserId();
    const guestRef = doc(db, "guests", guestId);
    
    await setDoc(guestRef, {
      guestId: guestId,
      lastActive: Date.now(),
      ...(appointmentId && { lastAppointmentId: appointmentId })
    }, { merge: true });
  } catch (error) {
    console.error("Error updating guest activity:", error);
  }
}

// Check if guest has an FCM token
export async function hasGuestFCMToken(): Promise<boolean> {
  try {
    const guestId = getGuestUserId();
    const guestRef = doc(db, "guests", guestId);
    const guestSnap = await getDoc(guestRef);
    
    return guestSnap.exists() && !!guestSnap.data()?.fcmToken;
  } catch (error) {
    console.error("Error checking guest FCM token:", error);
    return false;
  }
}
